import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  CircularProgress,
  Button,
  Chip,
  TextField,
  InputAdornment,
  alpha,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { colors, shadows } from "../source/source.styles";
import PhoneIcon from "@mui/icons-material/Phone";
import ChatIcon from "@mui/icons-material/Chat";
import SearchIcon from "@mui/icons-material/Search";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { getRecentConversionsApi } from "../../../api/leads.api";

const RecentConversions = ({ limit = 5, dateRange }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [conversionsData, setConversionsData] = useState([]);

  // Create a stable key for date range to trigger updates
  const dateRangeKey = useMemo(() => {
    if (dateRange?.startDate && dateRange?.endDate) {
      return `${dateRange.startDate.getTime()}-${dateRange.endDate.getTime()}`;
    }
    return "";
  }, [dateRange]);

  useEffect(() => {
    const fetchRecentConversions = async () => {
      try {
        setLoading(true);
        const params = { limit };
        if (dateRange?.startDate && dateRange?.endDate) {
          params.startDate = dateRange.startDate.toISOString();
          params.endDate = dateRange.endDate.toISOString();
        }
        const response = await getRecentConversionsApi(params);
        // Response structure: { data: { data: [...], total: 5 } }
        const data = response?.data?.data?.data || [];
        setConversionsData(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching recent conversions:", err);
        setConversionsData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentConversions();
  }, [limit, dateRangeKey]);

  // Filter data based on search query with safety check
  const filteredConversions = Array.isArray(conversionsData)
    ? conversionsData.filter(
        (conversion) =>
          conversion?.leadName
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          conversion?.source
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          conversion?.treatmentType
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          conversion?.telecaller?.name
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          conversion?.status?.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : [];

  const headerCellSx = {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.5,
    color: colors.textMuted,
    textTransform: "uppercase",
    borderBottom: `1px solid ${colors.borderLight}`,
    py: 2,
    bgcolor: colors.bgPrimary,
  };

  const bodyCellSx = {
    fontSize: 13,
    fontWeight: 500,
    color: colors.textPrimary,
    borderBottom: `1px solid ${colors.borderLight}`,
    py: 2.5,
  };

  const getAvatarColor = (name) => {
    const avatarColors = [
      "#C4B5FD",
      "#FCA5A5",
      "#A7F3D0",
      "#FDE68A",
      "#BAE6FD",
    ];
    const index = name.charCodeAt(0) % avatarColors.length;
    return avatarColors[index];
  };

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 3,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: "#D1FAE5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TrendingUpIcon sx={{ fontSize: 20, color: "#10B981" }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                color: colors.textPrimary,
              }}
            >
              Recent Conversions
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                color: colors.textSecondary,
              }}
            >
              Latest won deals and successful conversions
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField
            placeholder="Search..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: colors.textMuted }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 200,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: 13,
                bgcolor: colors.bgPrimary,
                "& fieldset": {
                  borderColor: colors.borderLight,
                },
                "&:hover fieldset": {
                  borderColor: colors.border,
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.palette.primary.main,
                  borderWidth: 1,
                },
              },
              "& .MuiOutlinedInput-input": {
                py: 1,
              },
            }}
          />
        </Stack>
      </Stack>

      {/* Table */}
      {loading ? (
        <Box
          sx={{
            py: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={32} sx={{ color: colors.primary }} />
        </Box>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Date</TableCell>
              <TableCell sx={headerCellSx}>Lead Name</TableCell>
              <TableCell sx={headerCellSx}>Contact</TableCell>
              <TableCell sx={headerCellSx}>Treatment</TableCell>
              <TableCell sx={headerCellSx}>Telecaller</TableCell>
              <TableCell sx={headerCellSx}>Revenue</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredConversions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ py: 8, textAlign: "center" }}>
                  <Typography
                    sx={{
                      fontSize: 16,
                      fontWeight: 600,
                      color: colors.textMuted,
                      mb: 1,
                    }}
                  >
                    No conversions found
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 13,
                      color: colors.textSecondary,
                    }}
                  >
                    {searchQuery
                      ? `No conversions matching "${searchQuery}"`
                      : "No recent conversions available"}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredConversions.map((conversion, index) => (
                <TableRow
                  key={index}
                  onClick={() => navigate(`/pipelines/${conversion.id}`)}
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      bgcolor: colors.bgPrimary,
                    },
                  }}
                >
                  <TableCell sx={bodyCellSx}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: colors.textSecondary,
                      }}
                    >
                      {conversion.date}
                    </Typography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: getAvatarColor(conversion.leadName),
                          fontSize: 13,
                          fontWeight: 600,
                        }}
                      >
                        {conversion.initials}
                      </Avatar>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: colors.textPrimary,
                        }}
                      >
                        {conversion.leadName}
                      </Typography>
                    </Stack>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          width: 28,
                          height: 28,
                          bgcolor: colors.bgPrimary,
                          "&:hover": { bgcolor: colors.border },
                        }}
                      >
                        <PhoneIcon
                          sx={{ fontSize: 14, color: colors.textMuted }}
                        />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          width: 28,
                          height: 28,
                          bgcolor: colors.bgPrimary,
                          "&:hover": { bgcolor: colors.border },
                        }}
                      >
                        <ChatIcon
                          sx={{ fontSize: 14, color: colors.textMuted }}
                        />
                      </IconButton>
                    </Stack>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {(
                        conversion.treatments || [conversion.treatmentType]
                      ).map((treatment, idx) => (
                        <Chip
                          key={idx}
                          label={
                            treatment
                              ? treatment.charAt(0).toUpperCase() +
                                treatment.slice(1)
                              : "N/A"
                          }
                          size="small"
                          sx={{
                            height: 24,
                            fontSize: 11,
                            fontWeight: 600,
                            bgcolor: alpha(theme.palette.primary.main, 0.15),
                            color: theme.palette.primary.main,
                          }}
                        />
                      ))}
                    </Stack>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          fontSize: 11,
                          bgcolor: colors.primary,
                        }}
                      >
                        {conversion.telecaller.name.charAt(0)}
                      </Avatar>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: colors.textPrimary,
                        }}
                      >
                        {conversion.telecaller.name}
                      </Typography>
                    </Stack>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "#10B981",
                      }}
                    >
                      ₹{conversion.revenue?.toLocaleString() || "0"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      {/* Footer */}
      {!loading && conversionsData.length > 0 && (
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${colors.borderLight}`,
            bgcolor: colors.bgPrimary,
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: colors.textSecondary,
              textAlign: "center",
            }}
          >
            Showing {filteredConversions.length} of {conversionsData.length}{" "}
            recent conversions
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default RecentConversions;
